import { useState } from "react";
import { Star, Check, Plus, ListPlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useWatchlists } from "@/hooks/useWatchlists";
import { toast } from "sonner";

// Star button + popover to toggle a player across the user's named watchlists.
export function WatchlistButton({ player }) {
  const { lists, limits, isGuest, addPlayer, removePlayer, createList, isWatched, isInList } = useWatchlists();
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const watched = isWatched(player.id);

  const toggle = async (listId) => {
    try {
      if (isInList(listId, player.id)) {
        await removePlayer(listId, player.id);
      } else {
        await addPlayer(listId, player);
        toast.success("Added to watchlist");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not update watchlist");
    }
  };

  const create = async () => {
    if (!newName.trim()) return;
    try {
      await createList(newName.trim());
      setNewName("");
      setCreating(false);
      toast.success("List created");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not create list");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="watchlist-toggle-btn"
          className={`rounded-lg h-10 font-semibold ${watched
            ? "bg-[#151B2B] border border-[#00E5A3]/40 text-[#00E5A3] hover:bg-[#1C2438]"
            : "bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F]"}`}>
          <Star size={16} className="mr-1.5" fill={watched ? "#00E5A3" : "none"} />
          {watched ? "Watching" : "Add to Watchlist"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 bg-[#0F1420] border-[#232D42] p-0" data-testid="watchlist-popover">
        <div className="px-4 py-3 border-b border-[#232D42] flex items-center gap-2">
          <ListPlus size={15} className="text-[#00E5A3]" />
          <span className="font-heading font-semibold text-slate-100 text-sm">Add to a watchlist</span>
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {lists.map((l) => {
            const inList = isInList(l.id, player.id);
            return (
              <button key={l.id} data-testid={`wl-list-toggle-${l.id}`} onClick={() => toggle(l.id)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#1C2438] text-left">
                <span className="text-sm text-slate-200 truncate">{l.name}
                  <span className="text-slate-600 font-mono ml-1.5">{l.players.length}/{limits.per_list}</span>
                </span>
                <span className={`h-5 w-5 rounded flex items-center justify-center border ${inList ? "bg-[#00E5A3] border-[#00E5A3]" : "border-[#2A3550]"}`}>
                  {inList && <Check size={13} className="text-[#04140F]" />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="border-t border-[#232D42] p-3">
          {creating ? (
            <div className="flex items-center gap-2">
              <input data-testid="wl-new-name-input" autoFocus value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder="List name…" maxLength={40}
                className="flex-1 bg-[#151B2B] border border-[#232D42] rounded-lg px-2.5 py-1.5 text-sm text-slate-200" />
              <Button data-testid="wl-new-create-btn" onClick={create} className="h-8 px-3 bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] text-xs font-semibold">Add</Button>
            </div>
          ) : (
            <button data-testid="wl-new-list-btn" onClick={() => (lists.length >= limits.lists
                ? toast.error(isGuest ? "Guest mode includes 1 list — create a free account for more." : `Your plan includes ${limits.lists} watchlist(s). Upgrade for more.`)
                : setCreating(true))}
              className="w-full flex items-center gap-2 text-sm text-[#00E5A3] hover:underline">
              <Plus size={14} /> New list <span className="text-slate-600 font-mono ml-auto">{lists.length}/{limits.lists}</span>
            </button>
          )}
        </div>
        {isGuest && (
          <p className="px-4 pb-3 text-[11px] text-slate-500 font-mono leading-relaxed">
            Saved on this device. Create a free account to sync & get alerts.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
